import React, { useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

// Wave 80 — consume the impersonation hash (#imp=<base64-json>) BEFORE React
// mounts, so the very first ProtectedRoute render already has the
// impersonation flag set in sessionStorage. Eliminates the "Forbidden" flash
// and application-review redirect when a new tab opens for "Act as Dealer".
(function _consumeImpersonationHash() {
    try {
        const h = typeof window !== "undefined" ? window.location.hash || "" : "";
        const m = /^#imp=([A-Za-z0-9+/=]+)$/.exec(h);
        if (!m) return;
        const decoded = JSON.parse(atob(m[1]));
        if (decoded && decoded.u) {
            window.sessionStorage.setItem("tc_impersonate_user_id", String(decoded.u));
            window.sessionStorage.setItem("tc_impersonate_name", String(decoded.n || "Dealer"));
            window.sessionStorage.setItem("tc_impersonate_supplier_id", String(decoded.s || ""));
        }
        // Strip the hash so a reload doesn't re-trigger (and the URL stays clean).
        history.replaceState(null, "", window.location.pathname + window.location.search);
    } catch { /* ignore — bad hash just means no impersonation */ }
})();
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import api from "./lib/api";
import AgreementGate from "./components/AgreementGate";
import BuyerTypeGate from "./components/BuyerTypeGate";
import { CityProvider } from "./context/CityContext";
import { CartProvider } from "./context/CartContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import AIChatWidget from "./components/AIChatWidget";
import CookieConsent from "./components/CookieConsent";
import FeedbackTab from "./components/FeedbackTab";
import ImpersonationBanner from "./components/ImpersonationBanner";
import Landing from "./pages/Landing";
import SearchPage from "./pages/Search";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Sell from "./pages/Sell";
import MPS from "./pages/MPS";
import PrintersGuide from "./pages/PrintersGuide";
import PrintersResults from "./pages/PrintersResults";
import Papers from "./pages/Papers";
import CustomerDashboard from "./pages/CustomerDashboard";
import SupplierDashboard from "./pages/SupplierDashboard";
import BulkImageUpload from "./pages/BulkImageUpload";
import AdminDashboard from "./pages/AdminDashboard";
import DealerProfile from "./pages/DealerProfile";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OAuthCallback from "./pages/OAuthCallback";
import OrderConfirmed from "./pages/OrderConfirmed";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Contact from "./pages/Contact";
import GetFeatured from "./pages/GetFeatured";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import About from "./pages/About";
import ProductDetail from "./pages/ProductDetail";
import NotFound from "./pages/NotFound";
import Bulk from "./pages/Bulk";
import Consumables from "./pages/Consumables";
import Scanners from "./pages/Scanners";
import Dealer from "./pages/Dealer";
import D2DProductDetail from "./pages/D2DProductDetail";
import DealerStore from "./pages/DealerStore";
import OEM from "./pages/OEM";
import CompatiblePage from "./pages/CompatiblePage";
import TonerRoute from "./pages/TonerRoute";
import ConsumableRoute from "./pages/ConsumableRoute";
import OemDashboard from "./pages/OemDashboard";
import ErrorBoundary from "./components/ErrorBoundary";
import VisitorTracker from "./components/VisitorTracker";
import ScrollToTop from "./components/ScrollToTop";
import { ProcAuthProvider } from "./context/ProcAuthContext";
import ProcProtectedRoute from "./components/ProcProtectedRoute";
import ProcurementLogin from "./pages/ProcurementLogin";
import ProcurementDashboard from "./pages/ProcurementDashboard";

// Procurement portal pages render full-screen with their own chrome, so the
// global marketplace Header/Footer/chat are hidden on /procurement routes.
function Chrome({ children }) {
    const { pathname } = useLocation();
    // The procurement DASHBOARD renders full-screen with its own chrome. The
    // public procurement LOGIN keeps the standard site Header/Footer so govt &
    // corporate users still see the TonersCart logo + nav and can get back home.
    const bare = pathname === "/procurement";
    return (
        <>
            <ImpersonationBanner />
            {!bare && <Header />}
            <ScrollToTop />
            <VisitorTracker />
            <main className="flex-1">
                <ErrorBoundary>{children}</ErrorBoundary>
            </main>
            {!bare && <Footer />}
            {!bare && <AIChatWidget />}
            {!bare && <FeedbackTab />}
            <CookieConsent />
            <Toaster
                richColors
                position="bottom-center"
                duration={2500}
                visibleToasts={2}
                offset={16}
                toastOptions={{ className: "tc-toast" }}
            />
        </>
    );
}

// Blocks ALL rendering until the initial auth check resolves, so logged-in
// users never briefly see the sign-in page on reload/navigation. Neutral
// full-screen loading state — no header, no content, no flicker.
function AuthGate({ children }) {
    const { loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center" data-testid="app-auth-gate">
                <div className="w-8 h-8 rounded-full border-2 border-black/10 border-t-[#00B7C7] animate-spin" aria-label="Loading" />
            </div>
        );
    }
    return children;
}

// Mounts the blocking agreement modal for logged-in Supabase users
// (customer / supplier / oem). Procurement users are gated separately.
//
// Wave 101 hotfix-5 — the full T&C/Seller Agreement popup must only appear
// for APPROVED dealers (their last gate before listing). Fresh dealers in
// the middle of onboarding (role=supplier but supplier_status != "approved")
// must NOT see this popup — the inline consent checkboxes in Steps 2 + 3
// cover their interim acknowledgements.
function SupabaseAgreementGate() {
    const { user, loading } = useAuth();
    const statusFn = useCallback(() => api.get("/agreements/status").then((r) => r.data), []);
    const acceptFn = useCallback(() => api.post("/agreements/accept").then(() => {}), []);
    const isSupplierAndNotApproved =
        user?.role === "supplier" && user?.supplier_status !== "approved";
    const ready =
        !loading
        && !!user
        && ["customer", "supplier", "oem"].includes(user.role)
        && !isSupplierAndNotApproved;
    return <AgreementGate ready={ready} statusFn={statusFn} acceptFn={acceptFn} />;
}

function App() {
    return (
        <HelmetProvider>
        <div className="App min-h-screen flex flex-col">
            <BrowserRouter>
                <CityProvider>
                    <CartProvider>
                        <AuthProvider>
                            <ProcAuthProvider>
                            <AuthGate>
                            <Chrome>
                                <Routes>
                                    <Route path="/" element={<Landing />} />
                                    <Route path="/search" element={<SearchPage />} />
                                    <Route path="/login" element={<Login />} />
                                    <Route path="/register" element={<Register />} />
                                    <Route path="/forgot-password" element={<ForgotPassword />} />
                                    <Route path="/reset-password" element={<ResetPassword />} />
                                    <Route path="/auth/callback" element={<OAuthCallback />} />
                                    <Route path="/cart" element={<Cart />} />
                                    <Route path="/checkout" element={<Checkout />} />
                                    <Route path="/sell" element={<Sell />} />
                                    <Route path="/mps" element={<MPS />} />
                                    <Route path="/printers" element={<PrintersResults />} />
                                    <Route path="/printers/guide" element={<PrintersGuide />} />
                                    <Route path="/printers/results" element={<PrintersResults />} />
                                    <Route path="/papers" element={<Papers />} />
                                    <Route path="/consumables" element={<Consumables />} />
                                    <Route path="/scanners" element={<Scanners />} />
                                    <Route path="/bulk" element={<Bulk />} />
                                    <Route path="/dealer" element={<Dealer />} />
                                    <Route path="/d2d/:kind/:id" element={<D2DProductDetail />} />
                                    <Route path="/store/:supplierId" element={<DealerStore />} />
                                    <Route path="/oem" element={<OEM />} />
                                    <Route path="/compatible/:slug" element={<CompatiblePage />} />
                                    <Route path="/terms" element={<Terms />} />
                                    <Route path="/privacy" element={<Privacy />} />
                                    <Route path="/contact" element={<Contact />} />
                                    <Route path="/get-featured" element={<GetFeatured />} />
                                    <Route path="/about" element={<About />} />
                                    <Route path="/toner/:id" element={<TonerRoute />} />
                                    <Route path="/printer/:id" element={<ProductDetail kind="printer" />} />
                                    <Route path="/paper/:id" element={<ProductDetail kind="paper" />} />
                                    <Route path="/consumable/:id" element={<ConsumableRoute />} />
                                    <Route path="/scanner/:id" element={<ProductDetail kind="scanner" />} />
                                    <Route path="/order-confirmed/:id" element={<OrderConfirmed />} />
                                    <Route path="/order-confirmed" element={<OrderConfirmed />} />
                                    <Route path="/procurement/login" element={<ProcurementLogin />} />
                                    <Route path="/procurement" element={<ProcProtectedRoute><ProcurementDashboard /></ProcProtectedRoute>} />
                                    <Route path="/customer" element={<ProtectedRoute roles={["customer"]}><CustomerDashboard /></ProtectedRoute>} />
                                    <Route path="/supplier" element={<ProtectedRoute roles={["supplier"]} allowApplicationStatus={["pending"]}><SupplierDashboard /></ProtectedRoute>} />
                                    <Route path="/supplier/bulk-images" element={<ProtectedRoute roles={["supplier"]}><BulkImageUpload /></ProtectedRoute>} />
                                    <Route path="/oem-dashboard" element={<ProtectedRoute roles={["oem"]}><OemDashboard /></ProtectedRoute>} />
                                    <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
                                    <Route path="/admin/dealers/:id" element={<ProtectedRoute roles={["admin"]}><DealerProfile /></ProtectedRoute>} />
                                    <Route path="*" element={<NotFound />} />
                                </Routes>
                            </Chrome>
                            <SupabaseAgreementGate />
                            <BuyerTypeGate />
                            </AuthGate>
                            </ProcAuthProvider>
                        </AuthProvider>
                    </CartProvider>
                </CityProvider>
            </BrowserRouter>
        </div>
        </HelmetProvider>
    );
}

export default App;
